import { useMemo, useState } from 'react'
import { Icon } from '@iconify/react'
import { AnimatePresence, motion } from 'framer-motion'
import {
  getFlavor,
  getFlavorName,
  getPairings,
  type Flavor,
  type FlavorLevel,
} from '../data'
import {
  getCategoryColor,
  getCategoryShort,
  getFlavorEmoji,
  getLevelColors,
} from '../data/flavorMeta'
import { getFlavorDescription } from '../data/flavorDescriptions'
import { getFlavorImage } from '../data/flavorImages'

interface FlavorDetailProps {
  flavor: Flavor
  onBack: () => void
  onOpen: (slug: string) => void
}

type PairView = 'table' | 'list'

/** Секции сочетаний по уровню смелости (в порядке от классики к смелым). */
const LEVEL_SECTIONS: { level: FlavorLevel; title: string }[] = [
  { level: 'classic', title: 'Классические сочетания' },
  { level: 'interesting', title: 'Необычные сочетания' },
  { level: 'bold', title: 'Смелые сочетания' },
]

/**
 * Карточка сочетания (product-card-md): фото сверху + имя + тег категории.
 * Кликабельна, если у вкуса есть своя страница (не «листовой» вкус графа).
 */
const PairingCard = ({
  slug,
  onOpen,
}: {
  slug: string
  onOpen: (slug: string) => void
}) => {
  const target = getFlavor(slug)
  const image = getFlavorImage(slug)
  const name = getFlavorName(slug)
  const category = target?.category

  const body = (
    <>
      <span className="pf-md__img">
        {image ? (
          <img src={image} alt="" loading="lazy" />
        ) : (
          <span
            className="pf-md__img-fallback"
            style={{
              background: category
                ? `${getCategoryColor(category)}33`
                : 'var(--bg-surface)',
            }}
            aria-hidden="true"
          >
            {getFlavorEmoji(slug)}
          </span>
        )}
      </span>
      <span className="pf-md__content">
        <span className="pf-md__name">{name}</span>
        {category && (
          <span className="pf-md__taste">
            <span
              className="pf-md__dot"
              style={{ background: getCategoryColor(category) }}
              aria-hidden="true"
            />
            <span className="pf-md__cat">{getCategoryShort(category)}</span>
          </span>
        )}
      </span>
    </>
  )

  if (!target) {
    return <div className="pf-md pf-md--static">{body}</div>
  }
  return (
    <button type="button" className="pf-md" onClick={() => onOpen(slug)}>
      {body}
    </button>
  )
}

/**
 * Экран одного вкуса: hero-карточка (фото, категория, доп. ноты, описание)
 * и сочетания, сгруппированные по уровню смелости. Клик по сочетанию
 * открывает страницу этого вкуса.
 */
const FlavorDetail = ({ flavor, onBack, onOpen }: FlavorDetailProps) => {
  const pairings = useMemo(() => getPairings(flavor), [flavor])
  const description = getFlavorDescription(flavor.slug)
  const image = getFlavorImage(flavor.slug)
  const [view, setView] = useState<PairView>('list')

  /** Сочетания по секциям + «прочие» (без уровня) в конце. */
  const sections = useMemo(() => {
    const byLevel = LEVEL_SECTIONS.map(s => ({
      ...s,
      color: getLevelColors(s.level).main,
      items: pairings.filter(p => p.level === s.level),
    }))
    const rest = pairings.filter(p => !p.level)
    if (rest.length) {
      byLevel.push({
        level: 'classic',
        title: 'Другие сочетания',
        color: getLevelColors(null).main,
        items: rest,
      })
    }
    return byLevel.filter(s => s.items.length > 0)
  }, [pairings])

  return (
    <div className="flavor-detail">
      <button type="button" className="flavors-back" onClick={onBack}>
        <Icon icon="solar:arrow-left-linear" width={20} height={20} />
        Все вкусы
      </button>

      <h1 className="flavor-detail__title">{flavor.name}</h1>

      {/* Hero-карточка вкуса */}
      <div className="flavor-hero">
        <div className="flavor-hero__img">
          {image ? (
            <img src={image} alt={flavor.name} />
          ) : (
            <span
              className="flavor-hero__img-fallback"
              style={{ background: `${getCategoryColor(flavor.category)}33` }}
              aria-hidden="true"
            >
              {getFlavorEmoji(flavor.slug)}
            </span>
          )}
        </div>

        <div className="flavor-hero__body">
          <div className="flavor-hero__cat">
            <span
              className="flavor-hero__cat-dot"
              style={{ background: getCategoryColor(flavor.category) }}
              aria-hidden="true"
            />
            {getCategoryShort(flavor.category)}
          </div>

          {description && (
            <div className="flavor-hero__block">
              <span className="flavor-hero__label">Описание вкуса</span>
              <p className="flavor-hero__desc">{description}</p>
            </div>
          )}
        </div>
      </div>

      {/* Тумблер «Таблица / Список» */}
      <div className="flavors-tabs" role="tablist" aria-label="Вид сочетаний">
        {(
          [
            { key: 'table', label: 'Таблица' },
            { key: 'list', label: 'Список' },
          ] as const
        ).map(tab => (
          <button
            key={tab.key}
            type="button"
            role="tab"
            aria-selected={view === tab.key}
            className={`flavors-tabs__btn ${view === tab.key ? 'active' : ''}`}
            onClick={() => setView(tab.key)}
          >
            {view === tab.key && (
              <motion.span
                layoutId="flavor-detail-pill"
                className="flavors-tabs__pill"
                transition={{ type: 'spring', stiffness: 420, damping: 34 }}
              />
            )}
            <span className="flavors-tabs__label">{tab.label}</span>
          </button>
        ))}
      </div>

      {/* Секции сочетаний по уровню */}
      <AnimatePresence mode="wait">
        <motion.div
          key={view}
          className="flavor-detail__sections"
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -8 }}
          transition={{ duration: 0.18, ease: 'easeOut' }}
        >
          {sections.map(section => (
            <section key={section.title} className="flavor-section">
              <h2 className="flavor-section__title">
                <span
                  className="flavor-section__dot"
                  style={{ background: section.color }}
                  aria-hidden="true"
                />
                {section.title}
                <span className="flavor-section__count">
                  {section.items.length}
                </span>
              </h2>

              {view === 'list' ? (
                <div className="flavors-md-grid">
                  {section.items.map(pair => (
                    <PairingCard
                      key={pair.slug}
                      slug={pair.slug}
                      onOpen={onOpen}
                    />
                  ))}
                </div>
              ) : (
                <ul className="pairings-list">
                  {section.items.map(pair => {
                    const target = getFlavor(pair.slug)
                    return (
                      <li key={pair.slug} className="pairing-row">
                        <span
                          className="pairing-row__dot"
                          style={{ background: section.color }}
                          aria-hidden="true"
                        />
                        {target ? (
                          <button
                            type="button"
                            className="pairing-row__name pairing-row__name--btn"
                            onClick={() => onOpen(pair.slug)}
                          >
                            {pair.name}
                          </button>
                        ) : (
                          <span className="pairing-row__name">{pair.name}</span>
                        )}
                        {target && (
                          <span className="pairing-row__cat">
                            {getCategoryShort(target.category)}
                          </span>
                        )}
                      </li>
                    )
                  })}
                </ul>
              )}
            </section>
          ))}
        </motion.div>
      </AnimatePresence>
    </div>
  )
}

export default FlavorDetail
